<template>
  <Dialog
    v-model:visible="visible"
    :modal="true"
    :closable="true"
    :showHeader="false"
    :style="{ width: '650px', maxHeight: '85vh', padding: 0, overflow: 'hidden' }"
    :breakpoints="{ '768px': '95vw' }"
    :contentStyle="{ padding: 0 }"
    class="capabilities-dialog-light"
  >
    <!-- Header azul -->
    <div class="modal-header-custom d-flex justify-content-between align-items-start p-3">
      <div>
        <h5 class="text-white fw-bold mb-1">Capacidades</h5>
        <span class="text-white-50" style="font-size: 0.9rem;">{{ exerciseTitle }}</span>
      </div>
      <button class="btn btn-link text-white p-0" @click="visible = false">
        <i class="bi bi-x-lg" style="font-size: 1.2rem;"></i>
      </button>
    </div>

    <!-- Body -->
    <div class="p-3">
      <!-- Filtro de curso -->
      <div class="filter-card mb-3 p-3">
        <label class="filter-label mb-1 d-block">
          {{ selectedCourse ? 'SELECIONE O CURSO PARA VISUALIZAR O CONTEXTO:' : 'FILTRO POR CURSO:' }}
        </label>
        <select v-model="selectedCourse" class="form-select" @change="onCourseChange">
          <option :value="null">Todos os Cursos Disponíveis</option>
          <option v-for="course in availableCourses" :key="course" :value="course">
            {{ course }}
          </option>
        </select>
      </div>

      <!-- Lista de capacidades -->
      <div class="capabilities-section" style="max-height: 400px; overflow-y: auto;">
        <!-- Sem capacidades -->
        <div v-if="capabilities.courses.length === 0 && !loading" class="text-muted text-center py-3">
          Nenhuma capacidade cadastrada para esta prática.
        </div>

        <!-- Modo "Todos" -->
        <template v-if="!selectedCourse && capabilities.courses.length > 0">
          <p class="fw-semibold text-dark mb-2">Capacidades vinculadas:</p>
          <div v-for="course in capabilities.courses" :key="course.course_name" class="mb-3">
            <div class="course-title-row mb-2">
              <i class="bi bi-grid me-1" style="font-size: 0.7rem;"></i>
              <span class="course-title">{{ course.course_name.toUpperCase() }}</span>
            </div>
            <div v-for="(cap, idx) in course.capabilities" :key="idx" class="capability-item mb-2">
              <span>{{ cap }}</span>
            </div>
          </div>
        </template>

        <!-- Modo curso específico -->
        <template v-if="selectedCourse && selectedCourseCaps">
          <p class="fw-semibold text-dark mb-2">Capacidades deste Curso:</p>
          <div v-for="(cap, idx) in selectedCourseCaps.capabilities" :key="idx" class="capability-item mb-2">
            <span>{{ cap }}</span>
          </div>

          <!-- Dica pedagógica -->
          <div v-if="isTeacherOrAbove" class="tip-card mt-3 p-3">
            <div v-if="tip.loading" class="d-flex align-items-center gap-2">
              <i class="pi pi-spinner pi-spin"></i>
              <span class="text-muted" style="font-size: 0.9rem;">Gerando dica pedagógica...</span>
            </div>
            <div v-else-if="tip.error">
              <span class="text-danger" style="font-size: 0.9rem;">Não foi possível gerar a dica no momento.</span>
              <button class="btn btn-sm btn-outline-primary ms-2" @click="generateTip">
                Tentar novamente
              </button>
            </div>
            <div v-else-if="tip.text">
              <div class="d-flex align-items-center gap-2 mb-2">
                <i class="bi bi-chat-square-text-fill text-primary"></i>
                <span class="fw-bold text-primary" style="font-size: 0.9rem;">Dica Pedagógica Personalizada</span>
              </div>
              <p class="m-0" style="font-size: 0.9rem; color: #333;">{{ tip.text }}</p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import capabilityService from '@/services/capability.service';
import { useAuthStore } from '@/stores/auth';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  exerciseId: { type: Number, required: true },
  exerciseTitle: { type: String, default: '' }
});

const emit = defineEmits(['update:modelValue']);

const authStore = useAuthStore();
const capabilities = ref({ courses: [] });
const selectedCourse = ref(null);
const tip = ref({ loading: false, text: null, error: false });
const loading = ref(false);

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const availableCourses = computed(() => capabilities.value.courses.map(c => c.course_name));

const selectedCourseCaps = computed(() => {
  if (!selectedCourse.value) return null;
  return capabilities.value.courses.find(c => c.course_name === selectedCourse.value);
});

const isTeacherOrAbove = computed(() => {
  const user = authStore.getLoggedInUsernameAndName();
  return user?.profile && user.profile !== 'STUDENT' && user.profile !== 'Estudante';
});

watch(() => props.modelValue, async (newVal) => {
  if (newVal && props.exerciseId) {
    await loadCapabilities();
  }
});

async function loadCapabilities() {
  loading.value = true;
  selectedCourse.value = null;
  tip.value = { loading: false, text: null, error: false };
  try {
    const { data } = await capabilityService.getCapabilities(props.exerciseId);
    capabilities.value = data;
  } catch {
    capabilities.value = { courses: [] };
  } finally {
    loading.value = false;
  }
}

function onCourseChange() {
  tip.value = { loading: false, text: null, error: false };
  if (selectedCourse.value && isTeacherOrAbove.value) {
    generateTip();
  }
}

async function generateTip() {
  tip.value = { loading: true, text: null, error: false };
  try {
    const { data } = await capabilityService.generateTip(props.exerciseId, selectedCourse.value);
    tip.value = { loading: false, text: data.tip, error: false };
  } catch {
    tip.value = { loading: false, text: null, error: true };
  }
}
</script>

<style scoped>
.modal-header-custom {
  background-color: #1A5FD5;
  border-radius: 8px 8px 0 0;
}

.filter-card {
  border: 2px solid #1A5FD5;
  border-radius: 8px;
  background: #f8faff;
}

.filter-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #1A5FD5;
  letter-spacing: 0.5px;
}

.course-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #555;
  letter-spacing: 0.5px;
}

.capability-item {
  padding: 10px 14px;
  border-left: 4px solid #1A5FD5;
  background: #f8f9fa;
  border-radius: 0 6px 6px 0;
  font-size: 0.9rem;
}

.tip-card {
  background: #f8faff;
  border-radius: 8px;
  border: 1px solid #e0e8f5;
}
</style>

<style>
.capabilities-dialog-light {
  color-scheme: light;
  background-color: #ffffff !important;
  color: #333333 !important;
}

.capabilities-dialog-light .p-dialog-content {
  padding: 0 !important;
  overflow: hidden;
  background-color: #ffffff !important;
  color: #333333 !important;
}

.capabilities-dialog-light .form-select {
  background-color: #ffffff !important;
  color: #333333 !important;
}
</style>
